
import { PlanningViewModel } from '../ViewModels';
import { PlanningHeader } from '../planning-config';
import { usePlanningEnricher } from './usePlanningEnricher';
import {
    ActionStatusHelper,
    DomainTableConfig,
    useAppEnum,
    ActionResponse,
    useLayoutLifeCycle,
} from '@/Shared';
import { DOMAIN_CONFIGS, DomainHookProps } from '@/Shared/Config/domain-config';
import { usePlanning } from '@/OneLiners';
import { PlanningModel } from '../Models/PlanningModel';


/**
 * usePlanningTableConfig hook
 *
 * Domain-specific layout composition hook for Planning entities.
 * Orchestrates domain data, enrichment, permissions, and UI table configuration.
 *
 * Responsibilities:
 * - Aggregate Planning domain state, enrichment, and permissions into a single response.
 * - Provide dynamic headers for Planning table.
 * - Map strict Model → ViewModel for UI consumption.
 * - Delegate lifecycle orchestration to useLayoutLifeCycle.
 *
 * @date: March 2, 2026
 * @author: Samano CASTRE
 *
 * @see DomainHookProps
 * @see DomainTableConfig<PlanningViewModel>
 *
 * @param props Domain-level configuration and permissions
 * @returns DomainTableConfig<PlanningViewModel>
 */
export function usePlanningTableConfig(props: DomainHookProps): DomainTableConfig<PlanningViewModel> {
    const { permissionHook, accessRight } = props;
    const { domainType } = DOMAIN_CONFIGS.Planning;

    const { PlanningEnums } = useAppEnum();

    const planningHook = usePlanning();

    /**
    * Enrichment layer
    * Resolves user, contact, and property data, and computes derived fields
    */
    const {
        userMap,
        contactMap,
        propertyMap,
        getStrictEnrichedPlanning,
        enricherResponse,
    } = usePlanningEnricher();

    /**
     * Combined readiness response
     * Only builds table once all sources succeeded
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            planningHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: planningHook.stateResponse.error ??
            enricherResponse.error
    };

    /**
       * Delegation to generic layout lifecycle.
       */
    const lifeCycleHook = useLayoutLifeCycle({
        response,
        domainType,
        props,
        entityHook: planningHook,

        /**
         * Header configuration for Property table.
         */
        headers: PlanningHeader.build(
            contactMap,
            propertyMap,
            userMap,
            PlanningEnums
        ),

        /**
         * Strict Model → ViewModel transformation.
         */
        toViewModelFn: (planning: PlanningModel, isDisplaying: boolean) =>
            new PlanningViewModel({
                planning: getStrictEnrichedPlanning(planning),
                viewEnums: PlanningEnums,
                accessRight,
                isDisplaying
            })
    });

    return lifeCycleHook;
}